// 09 — Feed the flock. The right ration for the birds you actually have, what
// it costs, and what happens if you get it wrong (REFOCUS §12a).
//
// The answer is the sim's: `correctFeedFor` feeds the youngest live bird, and
// `feedTypeForStage` is the same table the chore and the minigame use.

import { t, pick } from '../../i18n';
import { buyAndPourFeed } from '../actions';
import { playFed } from '../sound';
import { button, div, h, link } from '../dom';
import { factCard } from '../components/factCard';
import { openSheet } from '../components/sheet';
import { openMinigameSheet } from './minigameSheet';
import {
  selectFeedView,
  type BagSize,
  type FeedType,
  type FeederShortfallView,
} from '../selectors';
import type { Screen, ScreenContext } from '../router';

export function feedScreen(): Screen<HTMLElement> {
  return {
    id: 'feed',
    render: (ctx: ScreenContext<HTMLElement>) => {
      const { store, shell } = ctx.services;
      // The screen is built once and the two choices live here: picking a feed
      // re-renders only the verdict box, never the whole screen.
      let chosen: FeedType | null = null;
      let bag: BagSize = 'small';

      const rationHost = div({ class: 'cf-row-grid cf-grid-3' });
      const verdictHost = div({});
      const bagHost = div({ class: 'cf-row-grid cf-grid-2' });
      const extraHost = div({ class: 'cf-stack' });
      const spaceHost = div({});
      const footHost = div({ class: 'cf-screen__foot' });

      const paint = (): void => {
        const view = selectFeedView(store.getState(), chosen, bag);

        // The shortfall sits above the ration, not below the bags: it is about
        // the yard the feed is going into, and a keeper should read it before
        // deciding what to pour.
        spaceHost.replaceChildren(...(view.shortfall ? [spaceNotice(ctx, view.shortfall)] : []));

        // The chosen ration is part of what this screen shows, so the three
        // ration buttons repaint with the verdict rather than only once.
        rationHost.replaceChildren(
          ...view.options.map((option) =>
            button(
              {
                class: 'cf-choice',
                attrs: { 'aria-pressed': String(chosen === option.type) },
                on: {
                  click: () => {
                    chosen = option.type;
                    paint();
                  },
                },
              },
              h('span', { class: 'cf-choice__title', text: option.label }),
              h('span', { class: 'cf-choice__sub', text: option.detail }),
            ),
          ),
        );

        verdictHost.replaceChildren(
          div(
            {
              class: `cf-verdict ${chosen === null ? '' : view.verdict?.correct ? 'cf-verdict--good' : 'cf-verdict--bad'}`,
            },
            chosen !== null && view.verdict
              ? h('p', { class: 'cf-verdict__title', text: t(view.verdict.titleKey) })
              : null,
            chosen !== null && view.verdict
              ? h('p', { class: 'cf-verdict__body', text: t(view.verdict.bodyKey, view.verdict.bodyParams) })
              : h('p', { class: 'cf-verdict__body', text: t('feed.pickOne') }),
          ),
        );

        bagHost.replaceChildren(
          ...view.bags.map((option) =>
            button(
              {
                class: 'cf-choice',
                attrs: { 'aria-pressed': String(option.size === view.chosenBag) },
                on: {
                  click: () => {
                    bag = option.size;
                    paint();
                  },
                },
              },
              h('span', { class: 'cf-choice__title', text: option.label }),
              h('span', {
                class: 'cf-choice__sub',
                text: t('feed.bagLine', { days: option.days, cost: option.cost }),
              }),
              h('span', {
                class: 'cf-choice__sub',
                text: t('feed.perDay', { perDay: option.perDay }),
              }),
            ),
          ),
        );

        const spoil = view.bags.find((option) => option.size === view.chosenBag)?.spoilKey;
        extraHost.replaceChildren(
          div(
            { class: 'cf-row' },
            div(
              { class: 'cf-stack cf-stack--tight cf-grow' },
              h('span', { class: 'cf-label', text: view.calcium.title }),
              h('span', { class: 'cf-small cf-muted', text: view.calcium.body }),
            ),
            h('span', { class: 'cf-small cf-sage', text: view.calcium.spot }),
          ),
        );
        if (spoil) {
          extraHost.appendChild(
            div(
              { class: 'cf-notice cf-notice--danger' },
              h('span', { class: 'cf-notice__text', text: t(spoil) }),
            ),
          );
        }

        footHost.replaceChildren(
          button({
            class: 'cf-btn cf-btn--primary',
            text: t('feed.buyAndPour', { cost: view.cost }),
            on: {
              click: () => {
                const result = buyAndPourFeed(store, {
                  type: chosen ?? view.correct,
                  bag: view.chosenBag,
                });
                if (!result.performed) {
                  if (result.messageKey) {
                    shell.toast(t(result.messageKey, result.messageParams));
                  }
                  return;
                }
                playFed(store);
                shell.toast(t('feed.poured', { feed: t(`feed.option.${result.poured}`) }));
                // The pour is the feed chore, so the day can close on this tap.
                if (result.reward > 0) {
                  shell.toast(t('chore.allDone', { reward: result.reward }));
                }
                ctx.router.back();
              },
            },
          }),
          // Feed Frenzy is this chore's minigame (REFOCUS §12). The bonus is
          // the game, never the gate: the pour above still works without it.
          button({
            class: 'cf-btn cf-btn--quiet',
            text: t('choreSheet.play.feed_mix'),
            on: { click: () => openMinigameSheet(ctx, 'feed_mix', 'feed') },
          }),
        );
      };

      const view = selectFeedView(store.getState(), chosen, bag);
      const screen = div(
        { class: 'cf-screen' },
        div(
          { class: 'cf-screen__head' },
          link({
            class: 'cf-btn cf-btn--link',
            text: t('coop.backToCoop'),
            on: { click: () => ctx.router.back() },
          }),
        ),
        h('h1', { class: 'cf-display', text: t('feed.title') }),
        div(
          { class: 'cf-row cf-stack--tight' },
          h('span', { class: 'cf-chip', text: t('feed.flockWeek', { flock: view.flockLabel, week: view.week }) }),
          h('span', {
            class: 'cf-chip',
            text: t('feed.binLeft', { days: view.binDaysLeft }),
          }),
        ),
        spaceHost,
        h('p', { class: 'cf-section', text: t('feed.whichFeed') }),
        rationHost,
        verdictHost,
        h('p', { class: 'cf-section', text: t('feed.bagSize') }),
        bagHost,
        extraHost,
        footHost,
      );
      paint();
      screen.tabIndex = -1;
      return screen;
    },
  };
}

/**
 * The BHV-16 shortfall, and the card behind "Why?".
 *
 * It says the division rather than a verdict, because the number is the lesson:
 * the rule is that every bird can reach the trough at once, and a keeper who
 * can do the division on their own fingers can tell the game when it is wrong.
 * The remedy is deliberately not named here — the game has no store screen, so
 * a bigger feeder is not something a player can be told to go and get. The
 * card's fold-out is where the real-world answer lives, for the parent reading
 * over their shoulder.
 */
function spaceNotice(ctx: ScreenContext<HTMLElement>, view: FeederShortfallView): HTMLElement {
  const { store, shell } = ctx.services;
  const fact = view.fact;
  const notice = div(
    { class: 'cf-notice cf-notice--danger' },
    h('span', { class: 'cf-notice__text', text: t(view.bodyKey, view.bodyParams) }),
  );
  if (!fact) return notice;
  notice.appendChild(
    button({
      class: 'cf-btn cf-btn--quiet cf-sheet__why',
      text: t('choreSheet.why'),
      on: {
        click: () => {
          const card = openSheet(shell, { title: pick(fact.title), onClose: () => undefined });
          card.body.replaceChildren(factCard(fact, store, card.close));
        },
      },
    }),
  );
  return notice;
}
